import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/http";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import { ListPlus, Trash2, Clock, CheckCircle2, Phone, Check, ChevronsUpDown, UserPlus, CalendarPlus, X } from "lucide-react";
import { toast } from "sonner";
import { fmtCurrency } from "@/lib/calc";
import { useAuth } from "@/context/AuthContext";
import { fmtDur } from "@/components/booking/ServiceLines";

const ClientPick = ({ clients, value, onPick, onNew, isNew }) => {
  const [open, setOpen] = useState(false);
  const sel = clients.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" className="h-10 w-full rounded-lg border border-input bg-background px-3 text-left text-sm flex items-center justify-between" data-testid="wl-client">
          <span className={sel || isNew ? "" : "text-muted-foreground"}>{sel ? sel.name : isNew ? "New client" : "Select client…"}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]" align="start">
        <Command>
          <CommandInput placeholder="Search name or phone…" data-testid="wl-client-search" />
          <CommandList>
            <CommandEmpty>No client found.</CommandEmpty>
            <CommandItem value="__new" onSelect={() => { onNew(); setOpen(false); }} data-testid="wl-client-new"><UserPlus className="mr-2 h-4 w-4" />New client (creates their file)</CommandItem>
            {clients.map((c) => (
              <CommandItem key={c.id} value={`${c.name} ${c.phone || ""}`} onSelect={() => { onPick(c.id); setOpen(false); }} data-testid={`wl-client-opt-${c.id}`}>
                <Check className={`mr-2 h-4 w-4 ${value === c.id ? "opacity-100" : "opacity-0"}`} />{c.name}<span className="ml-auto text-xs text-muted-foreground">{c.phone}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const ServiceMulti = ({ services, groups, value, onChange, cur }) => {
  const [open, setOpen] = useState(false);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const picked = value.map((id) => services.find((s) => s.id === id)).filter(Boolean);
  return (
    <div className="space-y-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" role="combobox" className="h-10 w-full rounded-lg border border-input bg-background px-3 text-left text-sm flex items-center justify-between" data-testid="wl-services">
            <span className={picked.length ? "" : "text-muted-foreground"}>{picked.length ? `${picked.length} service${picked.length > 1 ? "s" : ""}` : "Services they want…"}</span>
            <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]" align="start">
          <Command>
            <CommandInput placeholder="Type a service…" data-testid="wl-services-search" />
            <CommandList>
              <CommandEmpty>No services found.</CommandEmpty>
              {groups.map(([cat, items]) => (
                <CommandGroup key={cat} heading={cat}>
                  {items.map((s) => (
                    <CommandItem key={s.id} value={s.name} onSelect={() => toggle(s.id)} data-testid={`wl-service-opt-${s.id}`}>
                      <Check className={`mr-2 h-4 w-4 shrink-0 ${value.includes(s.id) ? "opacity-100" : "opacity-0"}`} />
                      <span className="flex-1 truncate">{s.name}</span>
                      <span className="text-xs text-muted-foreground ml-2 shrink-0">{s.duration_min}m · {fmtCurrency(s.price, cur, 0)}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="wl-picked-services">
          {picked.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs font-medium pl-2.5 pr-1 py-1">
              {s.name} · {s.duration_min}m
              <button type="button" onClick={() => toggle(s.id)} className="h-4 w-4 rounded-full hover:bg-primary/20 flex items-center justify-center" aria-label="Remove"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Waitlist() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cur = user?.business?.currency || "AUD";
  const [entries, setEntries] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ client_id: "", client_name: "", client_phone: "", client_email: "", service_ids: [], preferred_date: "", note: "" });
  const [isNew, setIsNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/waitlist").then(({ data }) => setEntries(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data || [])).catch(() => {});
    api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => {});
  }, []);
  const groups = useMemo(() => {
    const cn = Object.fromEntries(categories.map((c) => [c.id, c.name]));
    const g = {};
    services.forEach((s) => { const k = (s.category_id && cn[s.category_id]) || "Other services"; (g[k] = g[k] || []).push(s); });
    return Object.entries(g);
  }, [services, categories]);

  const add = async () => {
    if (!form.client_id && !(form.client_name && form.client_phone)) { toast.error("Select a client, or enter a name and phone"); return; }
    if (!form.service_ids.length) { toast.error("Pick at least one service"); return; }
    setBusy(true);
    try {
      await api.post("/waitlist", form);
      setForm({ client_id: "", client_name: "", client_phone: "", client_email: "", service_ids: [], preferred_date: "", note: "" }); setIsNew(false);
      toast.success(isNew ? "Added — client file created" : "Added to waitlist");
      load();
      if (isNew) api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => {});
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't add"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/waitlist/${id}`); setEntries((e) => e.filter((x) => x.id !== id)); toast.success("Removed"); }
    catch { toast.error("Couldn't remove"); }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-8" data-testid="waitlist-page">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight flex items-center gap-2"><Clock className="h-7 w-7 text-primary" />Waitlist</h1>
        <p className="text-muted-foreground mt-1">Clients waiting for a spot. Tap <b>Book</b>, pick a time on the diary and save — their details and services drop straight in. When a booking is cancelled, everyone waiting for that day whose services fit the gap gets a text with a one-tap booking link — first to tap gets it.</p>
      </div>

      <Card className="rounded-2xl border p-5 shadow-sm mb-6">
        <div className="font-heading font-semibold mb-3 flex items-center gap-2"><ListPlus className="h-5 w-5 text-primary" />Add someone to the waitlist</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <ClientPick clients={clients} value={form.client_id} isNew={isNew}
            onPick={(id) => { setIsNew(false); setForm({ ...form, client_id: id, client_name: "", client_phone: "" }); }}
            onNew={() => { setIsNew(true); setForm({ ...form, client_id: "" }); }} />
          <ServiceMulti services={services} groups={groups} value={form.service_ids} onChange={(v) => setForm({ ...form, service_ids: v })} cur={cur} />
          {isNew && <Input placeholder="Client name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="h-10 rounded-lg" data-testid="wl-name" autoFocus />}
          {isNew && <Input placeholder="Mobile" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} className="h-10 rounded-lg" data-testid="wl-phone" />}
          <Input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="h-10 rounded-lg" data-testid="wl-date" title="Preferred day (optional)" />
          <Input placeholder="Optional note (e.g. flexible mornings)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="h-10 rounded-lg text-sm" data-testid="wl-note" />
        </div>
        <div className="flex justify-end mt-3">
          <Button className="h-10 rounded-lg" onClick={add} disabled={busy} data-testid="wl-add">{busy ? "Adding…" : "Add to waitlist"}</Button>
        </div>
      </Card>

      <div className="space-y-2" data-testid="waitlist-entries">
        {entries.length === 0 && <div className="text-sm text-muted-foreground text-center py-10 border border-dashed rounded-xl">Nobody on the waitlist right now.</div>}
        {entries.map((e) => {
          const svcs = e.services?.length ? e.services : (e.service_name ? [{ id: e.service_id, name: e.service_name }] : []);
          const mins = e.total_min || svcs.reduce((t, s) => t + (s.duration_min || 0), 0);
          return (
            <Card key={e.id} className="rounded-xl border p-4 flex items-center justify-between gap-3" data-testid={`wl-entry-${e.id}`}>
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2">
                  <button type="button" onClick={() => e.client_id && navigate(`/clients?client=${e.client_id}`)} className={e.client_id ? "hover:underline" : ""} data-testid={`wl-client-link-${e.id}`}>{e.client_name}</button>
                  {e.status === "notified"
                    ? <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1" title={e.last_offer ? `Offered ${e.last_offer.start?.slice(0, 16).replace("T", " ")}` : ""} data-testid={`wl-offered-${e.id}`}><CheckCircle2 className="h-3 w-3" />{e.last_offer ? `Offered ${new Date(e.last_offer.start.slice(0, 16)).toLocaleString("en-AU", { weekday: "short", hour: "numeric", minute: "2-digit" })}` : "Notified"}</span>
                    : <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Waiting</span>}
                </div>
                <div className="text-sm text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                  <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{e.client_phone}</span>
                  <span>{e.preferred_date ? `Wants: ${e.preferred_date}` : "Any day"}</span>
                  {mins > 0 && <span>{fmtDur(mins)}</span>}
                </div>
                {svcs.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`wl-entry-services-${e.id}`}>
                    {svcs.map((s, i) => <span key={s.id || i} className="text-xs rounded-full bg-muted px-2 py-0.5">{s.name}</span>)}
                  </div>
                )}
                {e.note && <div className="text-xs text-muted-foreground italic mt-1">{e.note}</div>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button size="sm" className="rounded-lg h-9" onClick={() => navigate(`/calendar?waitlist=${e.id}`)} data-testid={`wl-book-${e.id}`}><CalendarPlus className="h-4 w-4 mr-1.5" />Book</Button>
                <button onClick={() => remove(e.id)} className="h-9 w-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive" data-testid={`wl-remove-${e.id}`} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
